<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button
          type="button"
          class="btn invoice-back-button"
          :aria-label="$t('back')"
          @click="$router.go(-1)"
        >
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
          <span>{{$t('back')}}</span>
        </button>
        <div
          v-if="invoice"
          class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3"
        >
          <h3 class="invoice-reference mb-0">{{ invoice.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button
              v-if="invoice.status != 'draft'"
              type="button"
              class="btn invoice-action-button invoice-action-button--primary"
              @click="ViewInvoice()"
            >
              <AppIcon name="bi-file-earmark-pdf" class="bi bi-file-earmark-pdf" aria-hidden="true" />
              <span>{{$t('view')}}</span>
            </button>
            <button
              v-if="helper.checkAccess('edit_invoices') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="editRow()"
            >
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" />
              <span>{{$t('edit')}}</span>
            </button>
            <button
              v-if="invoice.status != 'draft' && helper.checkAccess('create_invoices') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="duplicateRow()"
            >
              <AppIcon name="bi-files" class="bi bi-files" aria-hidden="true" />
              <span>{{$t('duplicate')}}</span>
            </button>
            <button
             v-if="invoice.status != 'draft' && helper.checkAccess('edit_invoices') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="openSendWithMsg()"
            >
              <AppIcon name="bi-envelope-paper" class="bi bi-envelope-paper" aria-hidden="true" />
              <span>{{$t('send_email')}}</span>
            </button>
            <div
              v-if="invoice.invoice_type !== 'credit_note'
                && !['draft', 'canceled'].includes(invoice.status)
                && helper.checkAccess('create_deliveries') > 0"
              class="btn-group invoice-action-group"
            >
              <button
                id="invoiceTransferButton"
                class="btn invoice-action-button invoice-action-button--secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" aria-hidden="true" />
                <span>{{$t('transfert')}}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="invoiceTransferButton">
                <li>
                  <button type="button" class="dropdown-item" @click="transferDelivery()">
                    <AppIcon name="bi-truck" class="bi bi-truck" aria-hidden="true" />
                    <span>{{$t('delivery')}}</span>
                  </button>
                </li>
              </ul>
            </div>
            <div class="btn-group invoice-action-group">
              <button
                id="invoiceMoreActionsButton"
                class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                :aria-label="$t('more')"
                :title="$t('more')"
              >
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="invoiceMoreActionsButton">
                <li>
                  <button type="button" class="dropdown-item" @click="openReminder">
                    <AppIcon name="bi-bell" class="bi bi-bell" aria-hidden="true" />
                    <span>{{$t('reminder')}}</span>
                  </button>
                </li>
                <li v-if="invoice.status != 'draft'">
                  <button type="button" class="dropdown-item" @click="toggleReorder">
                    <AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" aria-hidden="true" />
                    <span>{{$t('reorder')}}</span>
                  </button>
                </li>
                <li v-if="invoice.status != 'draft'"><hr class="dropdown-divider"></li>
                <li v-if="invoice.status != 'draft' && helper.checkAccess('edit_invoices') != -1 && invoice.status != 'canceled'">
                  <button type="button" class="dropdown-item invoice-menu-action--warning" @click="cancelInvoice()">
                    <AppIcon name="bi-x-circle" class="bi bi-x-circle" aria-hidden="true" />
                    <span>{{$t('cancel')}}</span>
                  </button>
                </li>
                <li
                  v-if="invoice.status != 'draft' && helper.checkAccess('edit_invoices') != -1 && invoice.status != 'canceled' && helper.checkAccess('delete_invoices') != -1"
                >
                  <hr class="dropdown-divider">
                </li>
                <li v-if="helper.checkAccess('delete_invoices') != -1">
                  <button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()">
                    <AppIcon name="bi-trash" class="bi bi-trash" aria-hidden="true" />
                    <span>{{$t('delete')}}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Invoice Info -->
      <div v-else-if="invoice && invoice.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_payments') != -1">
            <a class="nav-link" :class="{active: step === 'payments'}" @click="step = 'payments'">{{$t('payments')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row document-overview-grid">
            <!-- Left Column: Invoice Details -->
            <div class="col-md-8 document-overview-main">
              <div class="card mb-4 document-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal v-if="invoice && invoice.id" inline :data="invoice" type="invoice" />
                </div>
              </div>
              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4 document-products-card">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div v-if="reorderMode" class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" />
                                {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
                                {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('price')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th>{{$t('vat')}}</th>
                        <th>{{$t('sub_total')}}</th>
                        <th>{{$t('total')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in invoice.products" :key="product.id">
                        <td><div class="product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{formatMoney(product.price)}}</td>
                        <td>{{product.quantity}}</td>
                        <td>{{product.vat}} %</td>
                        <td>{{formatMoney(product.sub_total)}}</td>
                        <td>{{formatMoney(product.total)}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↑'"
                                  :title="$t('reorder') + ' ↑'"
                                  :disabled="index === 0"
                                  @click="moveUp(index)"
                                >
                                  <AppIcon name="bi-arrow-up" class="bi bi-arrow-up" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↓'"
                                  :title="$t('reorder') + ' ↓'"
                                  :disabled="index === invoice.products.length - 1"
                                  @click="moveDown(index)"
                                >
                                  <AppIcon name="bi-arrow-down" class="bi bi-arrow-down" aria-hidden="true" />
                                </button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="invoice.notes" class="card document-notes-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ invoice.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Client & Balance Info -->
            <div class="col-md-4 document-overview-sidebar">
              <div class="card mb-4 document-info-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text me-2" />{{$t('invoice')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('reference')}}</small><strong>{{invoice.reference}}</strong></div>
                    <div class="col-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <div v-if="helper.checkAccess('edit_invoices') != -1 && invoice.status !== 'canceled'" class="dropdown">
                        <button id="invoice-details-status" type="button" class="badge border-0 dropdown-toggle" :class="getInvoiceStatusBadgeClass(invoice.status)" data-bs-toggle="dropdown" aria-expanded="false" :disabled="isInvoiceStatusDropdownDisabled(invoice)">
                          <span v-if="statusUpdatingInvoiceId === invoice.id" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>{{ $t(invoice.status) }}
                        </button>
                        <ul class="dropdown-menu shadow" aria-labelledby="invoice-details-status">
                          <li v-for="status in getInvoiceStatusOptions(invoice)" :key="status">
                            <button type="button" class="dropdown-item d-flex align-items-center justify-content-between gap-3" :class="{ active: invoice.status === status }" :disabled="isInvoiceStatusOptionDisabled(invoice, status)" @click="changeInvoiceStatus(invoice, status)">
                              <span>{{ $t(status) }}</span><AppIcon name="bi-check-lg" v-if="invoice.status === status" class="bi bi-check-lg" />
                            </button>
                          </li>
                        </ul>
                      </div>
                      <span v-else class="badge" :class="getInvoiceStatusBadgeClass(invoice.status)">{{ $t(invoice.status) }}</span>
                    </div>
                    <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('date')}}</small><strong>{{formatDate(invoice.date)}}</strong></div>
                    <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_by')}}</small><strong>{{invoice.user ? invoice.user.name : '-'}}</strong></div>
                    <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_at')}}</small><strong>{{formatDate(invoice.created_at)}}</strong></div>
                    <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('updated_at')}}</small><strong>{{formatDate(invoice.updated_at)}}</strong></div>
                  </div>
                </div>
              </div>
              <div v-if="reminders.length" class="card mb-4">
                <div class="card-header bg-white d-flex align-items-center justify-content-between">
                  <h6 class="mb-0"><AppIcon name="bi-bell" class="bi bi-bell me-2" />{{$t('scheduled_reminders')}}</h6>
                  <span class="badge rounded-pill bg-primary">{{reminders.length}}</span>
                </div>
                <div class="card-body">
                  <div v-for="reminder in reminders" :key="reminder.id" class="d-flex gap-3 pb-3 mb-3 border-bottom">
                    <AppIcon name="bi-clock" class="bi bi-clock text-primary mt-1" />
                    <div class="flex-grow-1">
                      <small class="text-muted d-block">{{$t('reminder_date')}}</small>
                      <strong class="d-block mb-1">{{formatDateTime(reminder.remind_at)}}</strong>
                      <small class="text-muted d-block">{{$t('reminder_message')}}</small>
                      <div style="white-space: pre-wrap; overflow-wrap: anywhere;">{{reminder.note}}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('client')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!invoice.client" class="text-muted">{{$t('no_client_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{invoice?.client?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{invoice?.client?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{invoice?.client?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{invoice?.client?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Balance Card -->
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-wallet2" class="bi bi-wallet2 me-2" />{{$t('balance')}}</h6>
                </div>
                <div class="card-body">
                  <div style="position: relative; height: 220px; margin-bottom: 20px;">
                    <canvas ref="invoiceBalanceChart" aria-label="Invoice Balance Chart" role="img"></canvas>
                  </div>
                  <div class="d-flex justify-content-between text-center">
                    <div>
                      <small class="text-dark d-block">{{$t('total')}}</small>
                      <small class="text-dark"><b>{{formatMoney(invoice.total)}}</b></small>
                    </div>
                    <div>
                      <small class="text-dark d-block">{{$t('amount_paid')}}</small>
                      <small class="text-dark"><b>{{formatMoney(amount_paid)}}</b></small>
                    </div>
                    <div>
                      <small class="text-dark d-block">{{$t('remaining')}}</small>
                      <small class="text-dark"><b>{{formatMoney(invoice.total - amount_paid)}}</b></small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Totals Card -->
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{$t('totals')}}</h6>
                </div>
                <div class="card-body">
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('sub_total')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(invoice.sub_total)}}</h5>
                  </div>
                  <div class="mb-3" v-if="invoice.total_discount > 0">
                    <small class="text-muted d-block">{{$t('total_discount')}}</small>
                    <h5 class="mb-0 text-muted">-{{formatMoney(invoice.total_discount)}}</h5>
                  </div>
                  <div class="mb-3" v-if="invoice.total_discount > 0">
                    <small class="text-muted d-block">{{$t('net_ht_after_discount')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney((Number(invoice.sub_total) || 0) - (Number(invoice.total_discount) || 0))}}</h5>
                  </div>
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('total_tax')}}</small>
                    <h5 class="mb-0 text-muted">{{formatMoney(invoice.total_vat)}}</h5>
                  </div>  
                  <div class="mb-3">
                    <small class="text-muted d-block">{{$t('total')}}</small>
                    <h5 class="text mb-0">{{formatMoney(total)}}</h5>
                  </div>
                  <div class="mb-0 pt-3 border-top">
                    <small class="text-muted d-block">{{$t('profit')}}</small>
                    <h5 class="mb-0" :class="totalProfit >= 0 ? 'text-success' : 'text-danger'">
                      {{formatBusinessMoney(totalProfit)}}
                    </h5>
                  </div>
                </div>
              </div>
              
            </div>  
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'payments' && helper.checkAccess('view_payments') != -1">
          <payment-component v-if="invoice && invoice.id" :invoice="invoice" :invoice_id="invoice.id" :client="invoice.client" :client_id="invoice.client_id" :isDetailSupplier="true"></payment-component>
        </div>

        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments :entity_id="invoice.id" entity_type="invoice" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>

        <!-- Edit Modal -->
        <add-edit-invoice-modal 
            v-if="editModalisOpen" 
            @modalClosed="editModalisOpen = false" 
            modalId="edit-invoice-modal-01" 
            :isModalOpen="editModalisOpen"  
            :data="invoice" 
            action="update" 
        ></add-edit-invoice-modal>
        <!-- Edit modal -->

        <!-- Status payment Modal -->
        <add-edit-payment-modal
            v-if="statusPaymentModalIsOpen"
            @modalClosed="statusPaymentModalIsOpen = false"
            modalId="invoice-details-status-payment-modal-01"
            :isModalOpen="statusPaymentModalIsOpen"
            :data="statusPayment"
            action="create"
            @refresh="handleStatusPaymentSaved"
        ></add-edit-payment-modal>
        <!-- Status payment modal -->
        
        <!-- Duplicate Modal -->
        <add-edit-invoice-modal 
            v-if="createModalisOpen" 
            @modalClosed="createModalisOpen = false" 
            modalId="create-invoice-modal-01" 
            :isModalOpen="createModalisOpen"  
            :data="invoiceToDuplicate" 
            action="create" 
        ></add-edit-invoice-modal>
        <!-- Duplicate modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalisOpen" 
            @modalClosed="invoiceModalisOpen = false" 
            modalId="invoice-invoice-modal-01" 
            :isModalOpen="invoiceModalisOpen"  
            :data="invoice" 
            type="invoice"
            :showSendForm="showSendForm"
            :defaultEmail="invoice?.client?.email || ''"
            :emailSent="emailSent"
            @sendEmail="sendEmailFromPreview">
        </invoice-template-modal>
        <!-- quote modal -->
        
        <!-- create Modal -->
        <add-edit-credit-invoice-modal 
            v-if="createCreditInvoiceModalState" 
            @modalClosed="createCreditInvoiceModalState = false"
            modalId="create-credit-invoice-modal-01" 
            :isModalOpen="createCreditInvoiceModalState" 
            :data="invoice" 
            action="transfer"
        ></add-edit-credit-invoice-modal>
        <!-- create modal -->

        <!-- Transfer to delivery modal -->
        <add-edit-delivery-modal
            v-if="createDeliveryModalState"
            @modalClosed="createDeliveryModalState = false"
            modalId="invoice-details-transfer-delivery-modal-01"
            :isModalOpen="createDeliveryModalState"
            :data="invoice"
            from="invoices"
            action="transfer"
        ></add-edit-delivery-modal>
        <!-- Transfer to delivery modal -->

        <!-- Send Email Modal -->
        <div class="modal fade" id="send-email-modal" tabindex="-1" aria-labelledby="send-email-modal-title" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 id="send-email-modal-title" class="modal-title">{{$t('send_email')}}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('close')"></button>
                    </div>
                    <div class="modal-body" style="text-align:left">
                        <div class="mb-3">
                            <label for="emailInput" class="form-label">{{$t('email')}} <span class="required">*</span></label>
                            <input v-model="emailForm.email" type="email" class="form-control" id="emailInput" :placeholder="$t('enter_email_address')" required>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn modal-action-button modal-action-button--secondary" data-bs-dismiss="modal">
                          <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
                          {{$t('close')}}
                        </button>
                        <button
                          type="button"
                          class="btn modal-action-button modal-action-button--primary"
                          :disabled="loading || !emailForm.email"
                          :aria-busy="loading ? 'true' : 'false'"
                          @click="sendEmail"
                        >
                          <span v-if="loading" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                          <AppIcon name="bi-send" v-else class="bi bi-send" aria-hidden="true" />
                          {{loading ? $t('sending') : $t('send')}}
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Send Email Modal -->

        <ReminderModal
          v-if="invoice && invoice.id"
          ref="reminderModal"
          entity-type="invoice"
          :entity-id="invoice.id"
        />

    </div>

</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import Chart from 'chart.js'

import AddEditInvoiceModal from '../../components/invoice/AddEditInvoiceModal';
import AddEditCreditInvoiceModal from '../../components/creditInvoice/AddEditInvoiceModal';
import AddEditDeliveryModal from '../../components/delivery/AddEditDeliveryModal';
import AddEditPaymentModal from '../../components/payment/AddEditPaymentModal.vue';
import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';
import DocumentHistory from '../../components/DocumentHistory.vue';
import ReminderModal from '../../components/ReminderModal.vue';

import attachments from '../../components/Attachment.vue'
import PaymentComponent from '../../components/PaymentComponent.vue';

export default {
  name: 'InvoiceDetails',
  components :{
    attachments,
    AddEditInvoiceModal,
    AddEditCreditInvoiceModal,
    AddEditDeliveryModal,
    AddEditPaymentModal,
    InvoiceTemplateModal,
    PaymentComponent,
    DocumentHistory,
    ReminderModal
  },
  data(){
    return {
        id : null,
        loading : true,
        invoice : null,
        editModalisOpen : false,
        createModalisOpen : false,
        invoiceToDuplicate : null,
        invoiceModalisOpen :false,
        reminders: [],
        loadingHistory : false,
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        step : 'overview',
        invoiceModal : null,
        editModal : null,
        createClientState : false,
        createCreditInvoiceModalState :false,
        createDeliveryModalState :false,
        emailForm : {
            email : ''
        },
        sendEmailModal : null,
        showSendForm: false,
        emailSent: false,
        invoiceBalanceChart: null,
        reorderMode: false,
        originalProductsOrder: null,
        statusPaymentModalIsOpen: false,
        statusPayment: null,
        statusPaymentInvoice: null,
        statusPaymentOutstanding: 0,
        statusUpdatingInvoiceId: null,
        invoiceStatusOptions: ['pending', 'partially_paid', 'paid', 'canceled']
    }
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/deliveries')
    this.getInvoice(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("invoice_details");

    //check permissions
    if(helper.checkAccess('view_invoices') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_invoices') == -2) return this.$router.push({path : '/subscribe'})

    // Initialize email modal
    // eslint-disable-next-line no-undef
    this.sendEmailModal = new bootstrap.Modal(document.getElementById('send-email-modal'))
    this.loadReminders()
    window.addEventListener('reminders:changed', this.loadReminders)

  },
  beforeDestroy(){
    window.removeEventListener('reminders:changed', this.loadReminders)
    if(this.invoiceBalanceChart){
      this.invoiceBalanceChart.destroy()
      this.invoiceBalanceChart = null
    }
  },
    methods : {
        openReminder(){
            this.$refs.reminderModal.open()
        },
        async loadReminders(){
            try {
                const query = new URLSearchParams({
                    entity_type: 'invoice',
                    entity_id: this.$route.params.id,
                    status: 'scheduled',
                    limit: '50'
                })
                const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders?${query.toString()}`)
                this.reminders = response.data.data || []
            } catch (e) {
                this.reminders = []
            }
        },
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
        ViewInvoice(){
            this.invoiceData = this.invoice 
            this.showSendForm = false
            this.emailSent = false
            this.invoiceModalisOpen = true;
        },
        openSendWithMsg(){
            this.invoiceData = this.invoice
            this.showSendForm = true
            this.emailSent = false
            this.invoiceModalisOpen = true
        },
        async sharableLink() {
            const response = await helper.customPost(`${process.env.VUE_APP_API}/external-documents`, {
                document_id: this.invoice.id,
                document_type: 'invoice'
            });

            const link = response.data.link;

            try {
                await navigator.clipboard.writeText(link); // Copy to clipboard
                this.$toast.open({
                    message: this.$t('Copied'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (err) {
                this.$toast.open({
                    message: err.message,
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async getInvoice(id){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/invoices/${id}`)
            this.invoice = response.data
            this.total = this.invoice.total
            this.amount_paid = this.invoice.transactions 
                ? this.invoice.transactions
                    .filter(t => t.status === 'paid')
                    .map(t => t.amount)
                    .reduce((a, c) => a + c, 0) 
                : 0
            this.loading = false
            this.$nextTick(() => this.buildInvoiceBalanceChart())
        }catch(error){
            this.$router.push({path : '/invoices'})
            this.loading = false
        }
        },
        getInvoiceStatusBadgeClass(status){
            return {
                'bg-info': status === 'draft',
                'bg-success': status === 'paid',
                'bg-warning': status === 'partially_paid',
                'bg-danger': status === 'canceled' || status === 'overdue',
                'bg-secondary': status === 'pending'
            }
        },
        getInvoiceTotal(invoice){
            const storedTotal = Number(invoice && invoice.total)
            if (Number.isFinite(storedTotal) && storedTotal > 0) return storedTotal

            return invoice && Array.isArray(invoice.products)
                ? invoice.products.reduce((sum, product) => sum + (Number(product.total) || 0), 0)
                : 0
        },
        getInvoicePaidAmount(invoice){
            if (invoice && Array.isArray(invoice.transactions) && invoice.transactions.length) {
                return invoice.transactions
                    .filter(transaction => transaction.status === 'paid')
                    .reduce((sum, transaction) => sum + (Number(transaction.amount) || 0), 0)
            }

            const storedAmount = Number(invoice && invoice.amount_paid)
            if (Number.isFinite(storedAmount)) return storedAmount
            return 0
        },
        getInvoiceOutstanding(invoice){
            return Math.max(0, this.getInvoiceTotal(invoice) - this.getInvoicePaidAmount(invoice))
        },
        requiresStatusPayment(invoice, status){
            return ['partially_paid', 'paid'].includes(status) && this.getInvoiceOutstanding(invoice) > 0
        },
        getInvoiceStatusOptions(invoice){
            return invoice.status === 'paid' ? ['canceled'] : this.invoiceStatusOptions
        },
        isInvoiceStatusDropdownDisabled(invoice){
            return this.statusUpdatingInvoiceId === invoice.id
                || invoice.status === 'canceled'
        },
        isInvoiceStatusOptionDisabled(invoice, status){
            if (invoice.status === status) return false
            if (status === 'partially_paid' && this.getInvoiceOutstanding(invoice) <= 0) return true

            return this.requiresStatusPayment(invoice, status)
                && helper.checkAccess('create_payments') === -1
        },
        async changeInvoiceStatus(invoice, status){
            if (this.isInvoiceStatusDropdownDisabled(invoice)
                || invoice.status === status
                || this.isInvoiceStatusOptionDisabled(invoice, status)) return

            if (this.requiresStatusPayment(invoice, status)) {
                this.openStatusPaymentModal(invoice, status)
                return
            }

            await this.updateInvoiceStatus(invoice, status, true)
        },
        openStatusPaymentModal(invoice, status){
            const outstanding = this.getInvoiceOutstanding(invoice)
            const total = this.getInvoiceTotal(invoice)

            this.statusPaymentInvoice = invoice
            this.statusPaymentOutstanding = outstanding
            this.statusPayment = {
                id: null,
                reference: '',
                date: moment().format('YYYY-MM-DD'),
                due_date: invoice.due_date ? moment(invoice.due_date).format('YYYY-MM-DD') : null,
                notes: '',
                client_id: invoice.client_id || (invoice.client && invoice.client.id) || null,
                client: invoice.client || {},
                invoice_id: invoice.id,
                invoice,
                transactions: invoice.transactions || [],
                payment_method: '',
                status: 'paid',
                total,
                amount_paid: status === 'paid' ? outstanding : 0,
                maximum_amount: outstanding,
                requested_invoice_status: status,
                lock_invoice_payment: true,
                lock_payment_amount: status === 'paid',
                hideTypeToggle: true,
                is_incoming: true,
                paymentable_type: 'invoice',
                paymentable_id: invoice.id
            }
            this.statusPaymentModalIsOpen = true
        },
        async handleStatusPaymentSaved(payment){
            const invoice = this.statusPaymentInvoice
            if (!invoice) return

            const paidNow = Number(payment && payment.amountPaid) || 0
            const status = paidNow >= this.statusPaymentOutstanding ? 'paid' : 'partially_paid'
            await this.updateInvoiceStatus(invoice, status, false)

            this.statusPaymentInvoice = null
            this.statusPayment = null
            this.statusPaymentOutstanding = 0
        },
        async updateInvoiceStatus(invoice, status, showToast){
            const previousStatus = invoice.status
            this.statusUpdatingInvoiceId = invoice.id
            this.$set(invoice, 'status', status)

            try{
                await helper.customPatch(
                    `${process.env.VUE_APP_API}/invoices/${invoice.id}`,
                    { status },
                    { showToast }
                )
            }catch(error){
                this.$set(invoice, 'status', previousStatus)
            }finally{
                await this.getInvoice(invoice.id)
                this.statusUpdatingInvoiceId = null
            }
        },
        buildInvoiceBalanceChart(){
            const ctx = this.$refs.invoiceBalanceChart
            if(!ctx){ return }
            if(this.invoiceBalanceChart){
              this.invoiceBalanceChart.destroy()
              this.invoiceBalanceChart = null
            }
            const total = Number(this.invoice && this.invoice.total) || Number(this.total) || 0
            const paid = Number(this.amount_paid) || 0
            const remaining = Math.max(total - paid, 0)
            const hasData = (paid + remaining) > 0
            const data = hasData ? [paid, remaining] : [1, 0]
            const labels = [this.$t('amount_paid'), this.$t('remaining')]
            this.invoiceBalanceChart = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels,
                    datasets: [{
                        data,
                        backgroundColor: ['#34c38f', '#ff6b6b'],
                        borderColor: ['#ffffff', '#ffffff'],
                        borderWidth: 2
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: { position: 'bottom' },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItem, chartData) => {
                                const idx = tooltipItem.index
                                const label = chartData.labels[idx]
                                const val = chartData.datasets[0].data[idx]
                                return `${label}: ${this.formatMoney ? this.formatMoney(val) : val}`
                            }
                        }
                    }
                }
            })
        },
        async getInvoiceHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/invoices/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;

            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/invoices/${this.invoice.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        editRow(){
            // this.clearForm()
            this.editModalisOpen = true
            this.invoice.date = moment(this.invoice.date).format('YYYY-MM-DD')
            // this.quoteProducts = this.quote.products
            this.calculatePrices()
        },
        selectTriggred(){
            this.calculatePrices()
        },
        calculatePrices(){
                this.$forceUpdate();
                for(let i = 0 ; i < this.invoice.products.length ; i++){
                    this.invoice.products[i].total = parseFloat(this.invoice.products[i].price) * parseFloat(this.invoice.products[i].quantity)
                    this.invoice.products[i].sub_total = Math.round(parseFloat(this.invoice.products[i].total))
                    if(this.invoice.products[i].vat > 0){
                        // this.invoice.products[i].total += helper.CustomToFixed(this.invoice.products[i].total * (parseFloat(this.invoice.products[i].vat) / 100))
                        this.invoice.products[i].total += parseFloat((parseFloat(this.invoice.products[i].total) * parseFloat(this.invoice.products[i].vat/ 100)).toFixed(2))
                        this.invoice.products[i].total = Math.round(this.invoice.products[i].total)
                    }
                }
                this.invoice.total = this.invoice.products && this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
        },
        parseTTctoHt(index,price){
            if(price && price > 0){
                this.invoice.products[index].price = ((price / this.invoice.products[index].quantity) / 1.2).toFixed(2)
                this.invoice.products[index].sub_total = parseFloat(this.invoice.products[index].price) * parseFloat(this.invoice.products[index].quantity)
            }
        },
        calculateItemPrice(){
            if(this.invoice.status == "draft") return 0
            return this.invoice.products && this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0; 
        },
        async updateRow(){
            try{
                this.loading = true
                this.invoice.products = helper.formatProducts(this.invoice.products, true)
                await helper.customPatch(`${process.env.VUE_APP_API}/invoices/${this.invoice.id}`,{...this.invoice })
                this.loading = false
                this.clearForm()
                this.editModal.hide()
                this.getInvoice(this.$route.params.id)
            }catch(error){
                this.loading = false
            }
        },
        clearForm(){
            this.invoice = {
                id : null,
                reference : '',
                client_id : null,
                client : {},
                date : '',
                total : 0,
                products : []
            }
        },
        async deleteRow(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/invoices/${this.invoice.id}`)
                    this.$toast.open({
                        message: this.$t('invoices_deleted_successfully'),
                        type: 'success',
                        position: 'bottom'
                    });
                    this.$router.push({path : '/invoices'})
                }
            })
        },
        async cancelInvoice(){
            this.$swal.fire({
                title: this.$t('cancel_invoice_confirmation'),
                text : this.$t('cancel_invoice_warning'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: this.$t('yes_cancel_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        this.loading = true
                        await helper.customPatch(`${process.env.VUE_APP_API}/invoices/${this.invoice.id}`, { status: 'canceled' })
                        await this.getInvoice(this.$route.params.id)
                        this.$toast.open({
                            message: this.$t('updated_successfully'),
                            type: 'success',
                            position: 'bottom'
                        });
                    } catch (error) {
                        this.loading = false
                        this.$toast.open({
                            message: error.response?.data?.message || this.$t('error_occurred'),
                            type: 'error',
                            position: 'bottom'
                        });
                    }
                }
            })
        },
        async duplicateRow(){
            try {
                // Copy the current invoice
                let copiedInvoice = JSON.parse(JSON.stringify(this.invoice));
                
                // Reset IDs and reference to let the modal handle draft creation
                copiedInvoice.id = null;
                copiedInvoice.reference = '';
                
                // Reset other fields for duplication
                copiedInvoice.date = moment().format('YYYY-MM-DD');
                copiedInvoice.due_date = null;
                // A duplicated invoice must start unpaid, regardless of the source status.
                copiedInvoice.status = 'pending';
                copiedInvoice.amount_paid = 0;
                copiedInvoice.transactions = [];
                copiedInvoice.pm_reference = null;
                copiedInvoice.is_recurring = false;
                copiedInvoice.recurrence_interval = null;
                copiedInvoice.next_recurrence_date = null;
                copiedInvoice.recurrence_source_id = null;
                copiedInvoice.recurrence_occurrence_date = null;
                
                if(copiedInvoice.products && copiedInvoice.products.length){
                    copiedInvoice.products.forEach(p => {
                        p.id = null;
                        p.invoice_id = null;
                    });
                } else {
                    copiedInvoice.products = [{
                        label: '',
                        id: null,
                        quantity: 0,
                        price: 0,
                        discount: 0,
                        vat: this.business.vat,
                        total: 0,
                        sub_total: 0,
                        custom_product: false
                    }];
                }
                
                this.invoiceToDuplicate = copiedInvoice;
                this.createModalisOpen = true;
            } catch (error) {
                helper.handleError(error);
            }
        },
        openCreateClientModal(){
            this.createClientState = !this.createClientState
        },
        createClient(data){
            if(data.client){
                this.invoice.client_id = data.client.id
                this.invoice.client = data.client
            }
            if(data && data.state != undefined){
                this.createClientState = data.state
            }
        },
        async transferCreditNote(){
            this.invoice.total = this.invoice.products && this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createCreditInvoiceModalState = !this.createCreditInvoiceModalState
        },
        transferDelivery(){
            this.invoice.total = this.invoice.products && this.invoice.products.length
                ? this.invoice.products.reduce((total, product) => total + (Number(product.total) || 0), 0)
                : 0;
            this.createDeliveryModalState = true
        },
        createCreditInvoiceModal(data){
            this.createCreditInvoiceModalState = data
        },
        formatDate(date) { return helper.formatDate(date) },
        formatDateTime(date) { return helper.formatDateTime(date) },
        formatMoney(number) {
            return helper.formatMoney(number, this.invoice?.currency || this.business?.currency)
        },
        formatBusinessMoney(number) {
            return helper.formatMoney(number, this.business?.currency)
        },
        formatReference(number,id) { return helper.formatReference(number,id);},
        openSendEmailModal(){
            this.emailForm.email = this?.invoice?.client?.email || ''
            this.sendEmailModal.show()
        },
        async sendEmail(){
            try{
                this.loading = true
                await helper.customPost(`${process.env.VUE_APP_API}/mails`, {
                    email: this.emailForm.email,
                    document_id: this.invoice.id,
                    document_type: 'invoice'
                })
                
                this.sendEmailModal.hide()
                this.$toast.open({
                    message: this.$t('email_sent_successfully'),
                    type: 'success',
                    position: 'bottom'
                })
                this.loading = false
            }catch(error){
                this.loading = false
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_sending_email'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async sendEmailFromPreview(payload){
            try{
                this.loading = true
                await helper.customPost(`${process.env.VUE_APP_API}/mails`, {
                    email: payload.email,
                    subject: payload.subject,
                    message: payload.message,
                    document_id: this.invoice.id,
                    document_type: 'invoice',
                    pdf_base64: payload.pdf_base64,
                    filename: payload.filename
                })
                
                this.emailSent = true
                this.loading = false
            }catch(error){
                this.loading = false
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_sending_email'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.invoice.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.invoice.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.invoice.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.invoice.products = products;
            }
        },
        moveDown(index) {
            if (index < this.invoice.products.length - 1) {
                const products = [...this.invoice.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.invoice.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                // Prepare products for saving
                // We only need to send the products array, the backend will handle the position based on index
                // but we need to ensure the structure matches what the backend expects for an update
                
                // Use existing update logic but only for reordering
                // We'll use the customPatch method similar to updateRow but without form validation overhead
                // and specifically targeting the products list update
                
                // Reuse the update endpoint
                const products = this.invoice.products.map(p => {
                    return {
                        id: p.id,
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: p.custom_product,
                        discount: p.discount,
                        position: p.position
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/invoices/${this.invoice.id}`, {
                    reference: this.invoice.reference,
                    date: this.invoice.date,
                    client_id: this.invoice.client_id,
                    order_id: this.invoice.order_id,
                    notes: this.invoice.notes,
                    status: this.invoice.status,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getInvoice(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }

    },
    computed : {
        helper() { return helper },
        business(){ return this.$store.getters.BUSINESS},
        totalProfit() {
            if (!this.invoice.products || this.invoice.products.length === 0) {
                return 0;
            }
            return this.invoice.products.reduce((total, product) => {
                const quantity = parseFloat(product.quantity) || 0;
                const price = parseFloat(product.price) || 0;
                const purchasePrice = parseFloat(product.purchase_price) || 0;
                return total + ((price - purchasePrice) * quantity);
            }, 0);
        }
        // total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
        // sub_total(){ return this.invoice.products.length ? this.invoice.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
    },
    watch : {
        '$route.params.id'(newId) {
            if (newId) {
                this.getInvoice(newId)
                this.loadReminders()
            }
        },
        editModalisOpen(newValue) {
            if(newValue === false){
                this.getInvoice(this.$route.params.id)
            }
        },
        step(newValue) {
            if(newValue === 'history' && this.invoice.id){
                this.getInvoiceHistory(this.invoice.id)
            }
            if(newValue === 'overview'){
                this.$nextTick(() => this.buildInvoiceBalanceChart())
            }
        }
    }
}
</script>

<style scoped>
.product-rich-text::v-deep > :last-child {
    margin-bottom: 0 !important;
}
</style>
